// ConceptMapNode — Custom React Flow node with progressive Bloom glow
//
// Renders as a pill-shaped chip. Visual intensity (border, fill, glow,
// text brightness) increases with Bloom level. Highlighted nodes get
// a pulsing ring in the session's accent color.

import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { BloomLevel } from '../../types';

interface ConceptMapNodeProps {
  data: {
    label: string;
    bloomLevel: BloomLevel;
    depth?: number;
    visualState?: 'dormant' | 'active' | 'highlighted';
    role?: string;
    [key: string]: unknown;
  };
}

// Static Tailwind class maps for progressive glow
const BLOOM_STYLES: Record<BloomLevel, {
  border: string;
  bg: string;
  text: string;
  glow: string;
}> = {
  recognize: {
    border: 'border-gray-600',
    bg: 'bg-transparent',
    text: 'text-gray-400',
    glow: '',
  },
  understand: {
    border: 'border-blue-400/30',
    bg: 'bg-blue-400/10',
    text: 'text-blue-300',
    glow: '',
  },
  apply: {
    border: 'border-emerald-400/40',
    bg: 'bg-emerald-400/15',
    text: 'text-emerald-300',
    glow: 'shadow-[0_0_8px_rgba(52,211,153,0.15)]',
  },
  analyze: {
    border: 'border-violet-400/40',
    bg: 'bg-violet-400/15',
    text: 'text-violet-300',
    glow: 'shadow-[0_0_12px_rgba(167,139,250,0.2)]',
  },
  evaluate: {
    border: 'border-pink-400/50',
    bg: 'bg-pink-400/20',
    text: 'text-pink-300',
    glow: 'shadow-[0_0_16px_rgba(244,114,182,0.2)]',
  },
  create: {
    border: 'border-amber-400/50',
    bg: 'bg-amber-400/20',
    text: 'text-amber-200',
    glow: 'shadow-[0_0_20px_rgba(251,191,36,0.25)]',
  },
};

// Visual state styling for dormant/active/highlighted states
const VISUAL_STATE_STYLES: Record<string, { opacity: string; labelVisible: boolean; extraClasses: string }> = {
  dormant: {
    opacity: 'opacity-20',
    labelVisible: false,
    extraClasses: 'border-dashed',
  },
  active: {
    opacity: 'opacity-100',
    labelVisible: true,
    extraClasses: 'transition-opacity duration-700 ease-in-out',
  },
  highlighted: {
    opacity: 'opacity-100',
    labelVisible: true,
    extraClasses: 'ring-2 ring-amber-400 ring-offset-1 ring-offset-gray-950 animate-pulse',
  },
};

function ConceptMapNode({ data }: ConceptMapNodeProps) {
  const { label, bloomLevel } = data;
  const styles = BLOOM_STYLES[bloomLevel] || BLOOM_STYLES.recognize;
  const visualState = (data.visualState as string) ?? 'active';
  const stateStyle = VISUAL_STATE_STYLES[visualState] ?? VISUAL_STATE_STYLES.active;

  // Variable sizing based on hierarchy depth
  const depth = data.depth ?? 1;
  const sizeClasses = depth === 0
    ? 'min-w-[140px] px-5 py-3 text-sm font-semibold'    // Root: largest
    : depth === 1
      ? 'min-w-[100px] px-4 py-2 text-xs font-medium'     // Subtopic: medium
      : 'min-w-[70px] px-3 py-1.5 text-[11px] font-normal'; // Detail: smallest

  return (
    <>
      <Handle type="target" position={Position.Top} className="!bg-gray-600 !w-2 !h-2 !border-0" />
      <div
        className={[
          'rounded-full border',
          'transition-all duration-300',
          sizeClasses,
          styles.border,
          styles.bg,
          styles.text,
          styles.glow,
          stateStyle.opacity,
          stateStyle.extraClasses,
        ].join(' ')}
      >
        {stateStyle.labelVisible ? (
          <span className="truncate text-center leading-tight">{label}</span>
        ) : (
          <span className="truncate text-center leading-tight text-transparent select-none">{label}</span>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-gray-600 !w-2 !h-2 !border-0" />
    </>
  );
}

export default memo(ConceptMapNode);
